import { Box, SystemStyleObject } from '@chakra-ui/react';
import { Select, SelectOption, SelectProps } from '@pluxee-design-system/core';
import { SkippableBaseControlProps } from '@pluxee-design-system/react-hook-form';
import { isArray } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useController } from 'react-hook-form';
import { MultiValue, PropsValue, SingleValue } from 'react-select';
import { getVariant } from './utils';

export interface SelectControlProps
  extends
    Omit<SelectProps, 'isDisabled' | 'name' | 'options' | 'variant'>,
    SkippableBaseControlProps {
  defaultOptionLabel?: string;
  disabled?: boolean;
  getNonOption?: (value: string) => SelectOption;
  id?: string;
  isArrayValue?: boolean;
  isCompact?: boolean;
  isFitContent?: boolean;
  isLoading?: boolean;
  isSearchable?: boolean;
  maxOptionsBeforeScroll?: number;
  options: readonly SelectOption[];
  onAfterChange?: (newValue: PropsValue<SelectOption>) => void;
  loadingText?: string;
  notFoundText?: string;
  readonly?: boolean;
  withDefaultOption?: boolean;
  sortAlphabetically?: boolean;
}

const getHeight = (isMulti?: boolean, size?: SelectProps['size']) => {
  if (isMulti) return 84;
  return size === 'sm' ? 28 : 48;
};

const getNonOptionFn = (value: string): SelectOption => ({ value, label: value });

const SelectControl = ({
  control,
  defaultOptionLabel = 'All',
  disabled,
  getNonOption = getNonOptionFn,
  id,
  isArrayValue = false,
  isCompact = false,
  isFitContent = false,
  isMulti,
  isSearchable = true,
  isReadOnly,
  name,
  loadingText = 'Getting data...',
  maxOptionsBeforeScroll,
  notFoundText = 'No options',
  onAfterChange,
  readonly,
  skipSuccess = false,
  skipVariant = false,
  sortAlphabetically = true,
  withDefaultOption,
  ...selectProps
}: SelectControlProps) => {
  const { field, fieldState, formState } = useController({
    name,
    control,
  });
  const { onChange } = field;
  const { error } = fieldState;
  const variant = getVariant(
    formState,
    fieldState,
    isReadOnly ?? readonly,
    skipSuccess,
    skipVariant,
  );
  const loadingFn = useMemo(() => () => loadingText, [loadingText]);
  const notFoundFn = useMemo(() => () => notFoundText, [notFoundText]);
  const handleChange = useCallback(
    (newValue: PropsValue<SelectOption>) => {
      if (isMulti) {
        onChange((newValue as MultiValue<SelectOption>).map((option) => option.value));
      } else {
        const newOption = newValue as SingleValue<SelectOption>;
        onChange(
          isArrayValue ? (newOption?.value ? [newOption?.value] : []) : (newOption?.value ?? null),
        );
      }
      onAfterChange?.(newValue);
    },
    [isArrayValue, isMulti, onAfterChange, onChange],
  );
  const options = useMemo(
    () =>
      sortAlphabetically
        ? selectProps.options?.slice()?.sort((a, b) => a.label?.localeCompare(b.label))
        : selectProps.options,
    [selectProps.options, sortAlphabetically],
  );
  const extendedOptions = useMemo(
    () =>
      withDefaultOption
        ? [{ value: null as unknown as string, label: defaultOptionLabel }, ...(options ?? [])]
        : options,
    [defaultOptionLabel, options, withDefaultOption],
  );
  const value = useMemo<PropsValue<SelectOption>>(() => {
    const val = isArrayValue && Array.isArray(field.value) ? field.value?.[0] : field.value;

    if (isMulti) {
      const normalizedValue = isArray(val) ? val : [val];
      const usedValues = new Set<string>();
      // add present options
      const result = options.filter(({ value: optionValue }) => {
        if (normalizedValue.includes(optionValue)) {
          usedValues.add(optionValue);
          return true;
        }
        return false;
      });
      // values not present in options are added
      normalizedValue.forEach((val) => {
        if (val && !usedValues.has(val)) {
          result.push(getNonOption(val as string) as SelectOption);
        }
      });
      return result as unknown as MultiValue<SelectOption>;
    }

    return (
      // not ===, only == compare '1' == 1
      (options?.find((o) => 'value' in o && o.value == val) as SelectOption) ??
      (val ? getNonOption(val as string) : null)
    );
  }, [getNonOption, isArrayValue, isMulti, field.value, options]);

  const compactSx: SystemStyleObject = isCompact
    ? { '.pluxee-select__control': { height: 'unset' } }
    : {};
  const fitContentSx: SystemStyleObject = isFitContent
    ? {
        '.pluxee-select__control': { height: 'fit-content !important' },
      }
    : {};
  const isSearchInput: SystemStyleObject = selectProps.isSearchInput
    ? {
        '.pluxee-select__control > .chakra-stack': { display: 'none' },
        '.pluxee-select__indicators button': { minWidth: 6 },
      }
    : {};

  return (
    // work-around to disable transparent background
    <Box
      sx={
        readonly
          ? {
              '.pluxee-select__control--is-disabled': { background: 'field !important' },
              ...compactSx,
              ...fitContentSx,
              ...isSearchInput,
            }
          : {
              '.pluxee-select__menu': { zIndex: 2 },
              ...compactSx,
              ...fitContentSx,
              ...isSearchInput,
            }
      }
    >
      <Select
        {...selectProps}
        options={withDefaultOption ? (value === null ? options : extendedOptions) : options}
        value={selectProps.value ?? value}
        isDisabled={disabled}
        variant={variant}
        onBlur={field.onBlur}
        onChange={selectProps.onChange ?? handleChange}
        helpText={error?.message ?? selectProps.helpText}
        inputId={id}
        isMulti={isMulti}
        isSearchable={isSearchable}
        noOptionsMessage={notFoundFn}
        loadingMessage={loadingFn}
        blurInputOnSelect={false} // see https://stackoverflow.com/questions/64610967/validation-works-incorrectly-for-react-select-with-formik-on-mobile-device
        maxMenuHeight={
          maxOptionsBeforeScroll
            ? getHeight(isMulti, selectProps.size) * maxOptionsBeforeScroll + 12
            : undefined
        }
      />
    </Box>
  );
};

export default SelectControl;
